import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Modal, Button, Form, Alert, Spinner, Row, Col, Card } from 'react-bootstrap';
import { assessmentReportStore } from '../stores/AssessmentReportStore';
import { assessmentStore } from '../stores/AssessmentStore';
import { userStore } from '../stores/UserStore';
import { academyStore } from '../stores/AcademyStore';
const StarRating = ({ value, onChange, disabled = false }) => {
    const stars = [2, 4, 6, 8, 10]; // Each star = 2 points
    const handleStarClick = (starValue) => {
        if (disabled)
            return;
        // Toggle: if clicking the same star, clear it (set to 0)
        onChange(value === starValue ? 0 : starValue);
    };
    const handleClear = () => {
        if (disabled)
            return;
        onChange(0);
    };
    return (_jsxs("div", { className: "d-flex align-items-center gap-2", children: [_jsx("div", { className: "d-flex gap-1", children: stars.map((starValue) => {
                    const isFilled = value >= starValue;
                    return (_jsx("button", { type: "button", className: "btn btn-link p-0 border-0", onClick: () => handleStarClick(starValue), disabled: disabled, style: {
                            color: isFilled ? '#ffc107' : '#6c757d',
                            fontSize: '1.5rem',
                            lineHeight: 1,
                            textDecoration: 'none'
                        }, title: `${starValue / 2} star${starValue / 2 > 1 ? 's' : ''}`, children: _jsx("i", { className: `bi ${isFilled ? 'bi-star-fill' : 'bi-star'}` }) }, starValue));
                }) }), value > 0 && (_jsx(Button, { variant: "outline-secondary", size: "sm", onClick: handleClear, disabled: disabled, type: "button", children: "Clear" })), value === 0 && (_jsx("span", { className: "text-muted small", children: "Not rated" }))] }));
};
const AssessmentReportDialog = observer(() => {
    const academyId = academyStore.currentAcademy?.id;
    const [formData, setFormData] = useState({
        playerId: '',
        reportDate: new Date().toISOString().split('T')[0],
        note: '',
        status: 'in-progress',
        defaultCoachId: ''
    });
    const [ratings, setRatings] = useState({});
    const [errors, setErrors] = useState({});
    // Fetch assessments and users when dialog opens
    useEffect(() => {
        if (assessmentReportStore.isDialogOpen && academyId) {
            if (assessmentStore.assessments.length === 0) {
                assessmentStore.fetchAssessments(academyId);
            }
            if (userStore.users.length === 0) {
                userStore.fetchUsers(academyId);
            }
        }
    }, [assessmentReportStore.isDialogOpen, academyId]);
    useEffect(() => {
        if (assessmentReportStore.isEditing && assessmentReportStore.selectedReport) {
            const report = assessmentReportStore.selectedReport;
            const firstCoachId = userStore.coaches[0]?.id || '';
            setFormData({
                playerId: report.playerId,
                reportDate: new Date(report.reportDate).toISOString().split('T')[0],
                note: report.note || '',
                status: report.status,
                defaultCoachId: report.ratings[0]?.coachId || firstCoachId
            });
            // Initialize ratings from report
            const ratingsMap = {};
            report.ratings.forEach(rating => {
                ratingsMap[rating.criteriaId] = {
                    rating: rating.rating,
                    coachId: rating.coachId
                };
            });
            setRatings(ratingsMap);
        }
        else {
            // Create mode - initialize with all criteria
            // Pre-fill playerId if preselected
            const firstCoachId = userStore.coaches[0]?.id || '';
            setFormData({
                playerId: assessmentReportStore.preselectedPlayerId || '',
                reportDate: new Date().toISOString().split('T')[0],
                note: '',
                status: 'done',
                defaultCoachId: firstCoachId
            });
            setRatings({});
        }
        setErrors({});
    }, [assessmentReportStore.isDialogOpen, assessmentReportStore.isEditing, assessmentReportStore.selectedReport, assessmentReportStore.preselectedPlayerId]);
    // Initialize ratings for all criteria when assessments are loaded
    useEffect(() => {
        if (!assessmentReportStore.isEditing && assessmentStore.assessments.length > 0) {
            const criteria = assessmentStore.assessments.filter(a => a.parentId !== null);
            const firstCoachId = formData.defaultCoachId || userStore.coaches[0]?.id || '';
            const initialRatings = {};
            criteria.forEach(criterion => {
                if (!ratings[criterion.id]) {
                    initialRatings[criterion.id] = {
                        rating: 0,
                        coachId: firstCoachId
                    };
                }
            });
            setRatings(prev => ({ ...prev, ...initialRatings }));
        }
    }, [assessmentStore.assessments, assessmentReportStore.isEditing]);
    // Update defaultCoachId when coaches are loaded and it's still empty
    useEffect(() => {
        if (userStore.coaches.length > 0 && !formData.defaultCoachId && !assessmentReportStore.isEditing) {
            const firstCoachId = userStore.coaches[0]?.id || '';
            if (firstCoachId) {
                setFormData(prev => ({
                    ...prev,
                    defaultCoachId: firstCoachId
                }));
                // Also update ratings that don't have a coachId
                setRatings(prev => {
                    const updated = { ...prev };
                    Object.keys(updated).forEach(criteriaId => {
                        if (!updated[criteriaId].coachId) {
                            updated[criteriaId].coachId = firstCoachId;
                        }
                    });
                    return updated;
                });
            }
        }
    }, [userStore.coaches, formData.defaultCoachId, assessmentReportStore.isEditing]);
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Update default coach for all ratings that don't have a specific coach
        if (name === 'defaultCoachId' && !assessmentReportStore.isEditing) {
            setRatings(prev => {
                const updated = { ...prev };
                Object.keys(updated).forEach(criteriaId => {
                    if (!updated[criteriaId].coachId || updated[criteriaId].coachId === formData.defaultCoachId) {
                        updated[criteriaId].coachId = value;
                    }
                });
                return updated;
            });
        }
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };
    const handleRatingChange = (criteriaId, rating) => {
        setRatings(prev => ({
            ...prev,
            [criteriaId]: {
                ...prev[criteriaId],
                rating
            }
        }));
    };
    const handleCoachChange = (criteriaId, coachId) => {
        setRatings(prev => ({
            ...prev,
            [criteriaId]: {
                ...prev[criteriaId],
                coachId
            }
        }));
    };
    const validateForm = () => {
        const newErrors = {};
        if (!formData.playerId) {
            newErrors.playerId = 'Player is required';
        }
        if (!formData.reportDate) {
            newErrors.reportDate = 'Report date is required';
        }
        if (!formData.defaultCoachId && !assessmentReportStore.isEditing) {
            newErrors.defaultCoachId = 'Default coach is required';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) {
            return;
        }
        if (!academyId) {
            setErrors({ submit: 'Academy ID is missing' });
            return;
        }
        // Get current user ID (for createdBy) - in a real app, this would come from auth
        // For now, we'll use the first coach or admin as a placeholder
        const createdBy = userStore.coaches[0]?.id || userStore.users.find(u => u.roles.includes('admin'))?.id || '';
        if (!createdBy) {
            setErrors({ submit: 'No user available to create report' });
            return;
        }
        // Convert ratings object to array
        const ratingsArray = Object.keys(ratings).map(criteriaId => ({
            criteriaId,
            rating: ratings[criteriaId].rating,
            coachId: ratings[criteriaId].coachId || formData.defaultCoachId
        }));
        try {
            const reportData = {
                playerId: formData.playerId,
                reportDate: formData.reportDate,
                createdBy,
                note: formData.note,
                status: formData.status,
                ratings: ratingsArray
            };
            if (assessmentReportStore.isEditing && assessmentReportStore.selectedReport) {
                await assessmentReportStore.updateReport(assessmentReportStore.selectedReport.id, reportData);
            }
            else {
                await assessmentReportStore.createReport(reportData);
            }
            assessmentReportStore.closeDialog();
        }
        catch (error) {
            // Error is handled by the store
        }
    };
    const handleClose = () => {
        assessmentReportStore.closeDialog();
    };
    const areas = assessmentStore.assessmentAreas;
    const coaches = userStore.coaches;
    const players = userStore.players;
    return (_jsxs(Modal, { show: assessmentReportStore.isDialogOpen, onHide: handleClose, size: "xl", children: [_jsx(Modal.Header, { closeButton: true, children: _jsx(Modal.Title, { children: assessmentReportStore.isEditing ? 'Edit Assessment Report' : 'Create Assessment Report' }) }), _jsxs(Form, { onSubmit: handleSubmit, children: [_jsxs(Modal.Body, { style: { maxHeight: '70vh', overflowY: 'auto' }, children: [assessmentReportStore.hasError && (_jsx(Alert, { variant: "danger", className: "mb-3", children: assessmentReportStore.error })), errors.submit && (_jsx(Alert, { variant: "danger", className: "mb-3", children: errors.submit })), _jsxs(Row, { className: "mb-3", children: [_jsx(Col, { md: 6, children: _jsxs(Form.Group, { children: [_jsx(Form.Label, { children: "Player *" }), _jsxs(Form.Select, { name: "playerId", value: formData.playerId, onChange: handleInputChange, isInvalid: !!errors.playerId, disabled: assessmentReportStore.isEditing || assessmentReportStore.isLoading, children: [_jsx("option", { value: "", children: "Select a player" }), players.map(player => (_jsx("option", { value: player.id, children: player.name }, player.id)))] }), _jsx(Form.Control.Feedback, { type: "invalid", children: errors.playerId })] }) }), _jsx(Col, { md: 6, children: _jsxs(Form.Group, { children: [_jsx(Form.Label, { children: "Report Date *" }), _jsx(Form.Control, { type: "date", name: "reportDate", value: formData.reportDate, onChange: handleInputChange, isInvalid: !!errors.reportDate, disabled: assessmentReportStore.isLoading }), _jsx(Form.Control.Feedback, { type: "invalid", children: errors.reportDate })] }) })] }), !assessmentReportStore.isEditing && (_jsxs(Row, { className: "mb-3", children: [_jsx(Col, { md: 6, children: _jsxs(Form.Group, { children: [_jsx(Form.Label, { children: "Default Coach *" }), _jsxs(Form.Select, { name: "defaultCoachId", value: formData.defaultCoachId, onChange: handleInputChange, isInvalid: !!errors.defaultCoachId, disabled: assessmentReportStore.isLoading, children: [_jsx("option", { value: "", children: "Select a coach" }), coaches.map(coach => (_jsx("option", { value: coach.id, children: coach.name }, coach.id)))] }), _jsx(Form.Text, { className: "text-muted", children: "This coach will be used for all ratings unless overridden per criteria" }), _jsx(Form.Control.Feedback, { type: "invalid", children: errors.defaultCoachId })] }) }), _jsx(Col, { md: 6, children: _jsxs(Form.Group, { children: [_jsx(Form.Label, { children: "Status" }), _jsxs(Form.Select, { name: "status", value: formData.status, onChange: handleInputChange, disabled: assessmentReportStore.isLoading, children: [_jsx("option", { value: "in-progress", children: "In Progress" }), _jsx("option", { value: "done", children: "Done" })] })] }) })] })), assessmentReportStore.isEditing && (_jsx(Row, { className: "mb-3", children: _jsx(Col, { md: 6, children: _jsxs(Form.Group, { children: [_jsx(Form.Label, { children: "Status" }), _jsxs(Form.Select, { name: "status", value: formData.status, onChange: handleInputChange, disabled: assessmentReportStore.isLoading, children: [_jsx("option", { value: "in-progress", children: "In Progress" }), _jsx("option", { value: "done", children: "Done" })] })] }) }) })), _jsx(Row, { className: "mb-3", children: _jsx(Col, { md: 12, children: _jsxs(Form.Group, { children: [_jsx(Form.Label, { children: "Note" }), _jsx(Form.Control, { as: "textarea", rows: 3, name: "note", value: formData.note, onChange: handleInputChange, placeholder: "Enter notes about this assessment...", disabled: assessmentReportStore.isLoading })] }) }) }), _jsx("hr", { className: "my-4" }), _jsx("h5", { className: "mb-3", children: "Rate Criteria" }), areas.length === 0 ? (_jsx(Alert, { variant: "info", children: "No assessment areas found. Please create assessment areas and criteria first." })) : (_jsx("div", { className: "mb-3", children: areas.map((area) => {
                                    const criteria = assessmentStore.getCriteriaForArea(area.id);
                                    if (criteria.length === 0)
                                        return null;
                                    return (_jsxs(Card, { className: "mb-3", children: [_jsx(Card.Header, { children: _jsx("strong", { children: area.name }) }), _jsx(Card.Body, { children: criteria.map((criterion) => {
                                                    const rating = ratings[criterion.id] || { rating: 0, coachId: formData.defaultCoachId };
                                                    return (_jsx("div", { className: "mb-3 pb-3 border-bottom", children: _jsxs("div", { className: "d-flex justify-content-between align-items-start mb-2", children: [_jsxs("div", { className: "flex-grow-1", children: [_jsx("strong", { children: criterion.name }), _jsx("div", { className: "mt-2", children: _jsx(StarRating, { value: rating.rating, onChange: (value) => handleRatingChange(criterion.id, value), disabled: assessmentReportStore.isLoading }) })] }), _jsx("div", { className: "ms-3", style: { minWidth: '150px' }, children: _jsx(Form.Select, { size: "sm", value: rating.coachId || '', onChange: (e) => handleCoachChange(criterion.id, e.target.value), disabled: assessmentReportStore.isLoading || !formData.defaultCoachId, children: coaches.map(coach => (_jsx("option", { value: coach.id, children: coach.name }, coach.id))) }) })] }) }, criterion.id));
                                                }) })] }, area.id));
                                }) }))] }), _jsxs(Modal.Footer, { children: [_jsx(Button, { variant: "secondary", onClick: handleClose, disabled: assessmentReportStore.isLoading, className: "btn-touch w-mobile-100", children: "Cancel" }), _jsxs(Button, { variant: "primary", type: "submit", disabled: assessmentReportStore.isLoading, className: "btn-touch w-mobile-100", children: [assessmentReportStore.isLoading && (_jsx(Spinner, { animation: "border", size: "sm", className: "me-2" })), assessmentReportStore.isEditing ? 'Update' : 'Create'] })] })] })] }));
});
export default AssessmentReportDialog;
